import { Box, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import PageCard from '../../../components/page-card';
import { translate } from '../../../i18n';
import { useGetUserAdditionalFields } from '../hooks/useGetUserAdditionalFields';

/**
 * Renders the leftover CSV columns a bulk import stashed on the user
 * (User.additionalFields) — e.g. "Ville", "Profil", "Date de création",
 * "Crédits de réservation restants". The keys are the CSV's own French headers,
 * so they display as-is (no translation).
 *
 * Imported-only surface: the card hides itself entirely for users with no such
 * fields (organic signups, or imports where every column mapped to a real
 * column) instead of showing an empty section on every user page.
 */
export default function UserAdditionalFieldsCard({ userId }: { userId: string }) {
    const { data, isLoading } = useGetUserAdditionalFields(userId);

    // Sort by key for a stable order: Postgres JSONB does not preserve the CSV's
    // column order, so alphabetical is the only deterministic display.
    const entries = Object.entries(data?.additionalFields ?? {}).sort(([a], [b]) =>
        a.localeCompare(b),
    );

    if (isLoading || entries.length === 0) return null;

    return (
        <PageCard title={translate('user.imported_csv_fields')}>
            <Box p={4} px={6}>
                <Table size="sm">
                    <Thead>
                        <Tr>
                            <Th>{translate('user.imported_field')}</Th>
                            <Th>{translate('user.imported_value')}</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {entries.map(([key, value]) => (
                            <Tr key={key}>
                                <Td fontWeight="medium">{key}</Td>
                                <Td>
                                    {value || (
                                        <Text as="span" color="gray.500">
                                            —
                                        </Text>
                                    )}
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </Box>
        </PageCard>
    );
}
